import { auditIdFromLocation } from '../navigation/audit-deep-link.js';

const REQUIRED_FIREBASE_FIELDS = Object.freeze({
  VITE_FIREBASE_API_KEY: 'apiKey',
  VITE_FIREBASE_AUTH_DOMAIN: 'authDomain',
  VITE_FIREBASE_PROJECT_ID: 'projectId',
  VITE_FIREBASE_APP_ID: 'appId',
});

const PLACEHOLDER_PATTERN = /^(replace-|your-|example\b)/i;
const TENANT_PATTERN = /^[a-z0-9](?:[a-z0-9_-]{0,62}[a-z0-9])?$/;

export class RuntimeConfigError extends Error {
  constructor(message, missing = []) {
    super(message);
    this.name = 'RuntimeConfigError';
    this.code = 'invalid-runtime-config';
    this.missing = missing;
  }
}

function clean(value) {
  return String(value == null ? '' : value).trim();
}

function tenantFromPath(locationValue) {
  const pathname = clean(locationValue?.pathname) || '/';
  const match = /^\/t\/([^/]+)(?:\/|$)/.exec(pathname);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch (_error) {
    throw new RuntimeConfigError('Il tenant nel percorso non è valido.', ['pathname']);
  }
}

/**
 * Reads public, build-time configuration. No API base URL is configurable on
 * purpose: the browser always talks to the Firebase Hosting same-origin /api
 * rewrite, avoiding a second CORS/auth surface.
 */
export function readRuntimeConfig(env = import.meta.env, locationValue = globalThis.location) {
  const firebase = {};
  const missing = [];

  Object.entries(REQUIRED_FIREBASE_FIELDS).forEach(([envKey, configKey]) => {
    const value = clean(env?.[envKey]);
    if (!value || PLACEHOLDER_PATTERN.test(value)) missing.push(envKey);
    else firebase[configKey] = value;
  });

  const tenantId = clean(tenantFromPath(locationValue) || env?.VITE_A11YPILOT_TENANT_ID);
  if (!tenantId || PLACEHOLDER_PATTERN.test(tenantId)) {
    missing.push('VITE_A11YPILOT_TENANT_ID');
  } else if (!TENANT_PATTERN.test(tenantId)) {
    throw new RuntimeConfigError(
      'VITE_A11YPILOT_TENANT_ID deve essere lowercase, lungo al massimo 64 caratteri e non può iniziare o terminare con trattini o underscore.',
      ['VITE_A11YPILOT_TENANT_ID'],
    );
  }

  if (missing.length) {
    throw new RuntimeConfigError(
      `Configurazione pubblica incompleta: ${missing.join(', ')}.`,
      missing,
    );
  }

  const appCheckSiteKey = clean(env?.VITE_FIREBASE_APPCHECK_SITE_KEY);
  if (appCheckSiteKey && PLACEHOLDER_PATTERN.test(appCheckSiteKey)) {
    throw new RuntimeConfigError(
      'VITE_FIREBASE_APPCHECK_SITE_KEY contiene ancora un valore di esempio.',
      ['VITE_FIREBASE_APPCHECK_SITE_KEY'],
    );
  }

  /* «Accedi con Justbit» è la via primaria e resta accesa se nessuno dice il
     contrario. Il flag esiste per poterla spegnere — per esempio prima che il
     provider `oidc.justbit` sia configurato sul progetto Firebase — non per
     doverla accendere. */
  const justbitSsoValue = clean(env?.VITE_ENABLE_JUSTBIT_SSO).toLowerCase();
  if (justbitSsoValue && !['true', 'false', '1', '0'].includes(justbitSsoValue)) {
    throw new RuntimeConfigError(
      'VITE_ENABLE_JUSTBIT_SSO deve essere true o false.',
      ['VITE_ENABLE_JUSTBIT_SSO'],
    );
  }

  return Object.freeze({
    firebase: Object.freeze(firebase),
    tenantId,
    auditId: auditIdFromLocation(locationValue),
    apiEndpoint: '/api',
    appCheckSiteKey,
    justbitSsoEnabled: !['false', '0'].includes(justbitSsoValue),
  });
}
